/* Pumpkin hunt (sections/pumpkin-hunt.liquid): places each pumpkin on its anchor, and runs the
   reveal on tap. The section carries the config as JSON and the pumpkin art as a template.

   Placing: a pumpkin is a child of its anchor element (the hero slide, a product card photo, ...),
   positioned by percentages of that element's box, so it keeps its spot at any screen size. An
   anchor that is not positioned gets position: relative through a class. In-text pumpkins mount
   on <span data-pumpkin="label"></span> markers wherever content holds them.

   Reveal: the page dims and stops scrolling, a copy of the pumpkin flies from where it sat to the
   middle of the screen (it grows on the way), then the card drops out from under it and the
   pumpkin stays as the card's pin. Found pumpkins are remembered in localStorage and shown a
   little dimmer, without the wobble; tapping one again reopens the card. In the theme editor
   nothing is remembered, so every pumpkin shows as unfound. */
(function () {
  if (customElements.get('pumpkin-hunt')) return;

  const STORE = 'fltc_pumpkins_found';
  const PIN = 104; /* px, the pumpkin as the card's pin */

  const ANCHORS = {
    hero: '.main-slideshow .slideshow__slide',
    about_banner: '.about-us-banner__media',
    rating_stars: '.fltc-rating-stars',
    review_stars: '.review-marquee--card:not(.is-clone) .star-rating',
    product_card: '.product-card .product-featured-image',
    announcement: '.announcement-bar',
    page_title: '.post-title, .page-title, main h1',
    /* the text itself, never its collapsing wrapper (a grid row that must stay empty to close) */
    collection_description: '.desc-collapse--inner, .max-height--inner-content, .template-header--description',
    article: '.blog-post-detail .post-title-wrapper',
    image_text: '.image-with-text--image',
    gallery: '.product-gallery-container',
    footer: 'footer',
    menu_card: '.mega-menu-promotion .mega-menu-promotion--image',
    search: null,
    custom: null,
    inline: null
  };

  /* letters and digits only, so "jack-o'-lantern", "Jack O Lantern" and "jackolantern" are one word */
  const squash = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

  /* anchors the theme renders more than once (the mega menu cards exist in the desktop menu and
     again in the phone drawer): the pumpkin mounts on every copy, since only one shows at a time */
  const MULTI = { menu_card: true };

  /* does this element say the words? A photo has no words, so the nearest ancestor that has any
     (the card around it) is the one asked, and no further: the list holding every card would match
     them all */
  const saysText = (el, text) => {
    const want = text.trim().toLowerCase();
    if (!want) return true;
    let node = el;
    for (let i = 0; i < 4 && node; i++) {
      const words = (node.textContent || '').trim().toLowerCase();
      if (words) return words.indexOf(want) > -1;
      node = node.parentElement;
    }
    return false;
  };

  class PumpkinHunt extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-pkh-config]').textContent);
      } catch (e) {
        console.warn('[pumpkin-hunt] config did not parse', e);
        return;
      }
      this.art = this.querySelector('[data-pkh-art]');
      this.stage = this.querySelector('[data-pkh-stage]');
      this.flyer = this.querySelector('[data-pkh-flyer]');
      this.card = this.querySelector('[data-pkh-card]');
      this.pin = this.querySelector('[data-pkh-pin]');
      this.found = this.config.designMode ? [] : this.readFound();
      this.placed = [];
      this.hidden = false;

      this.stage.querySelectorAll('[data-pkh-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      /* the coupon rows are built per reveal, so one listener on the card serves their copy buttons */
      this.card.addEventListener('click', (e) => { const b = e.target.closest('[data-pkh-copy]'); if (b) this.copy(b); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.stage.hidden) this.close(); });

      const place = () => this.placeAll();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', place);
      else place();
      /* sections re-render in the editor and some carousels build late; look again after load */
      window.addEventListener('load', () => setTimeout(place, 300));
      document.addEventListener('shopify:section:load', () => setTimeout(place, 50));
      /* some anchors arrive later still: the phone drawer keeps its sub-panels (mega menu cards
         included) in a template until that menu item is first opened. Any added content gets a
         fresh look, a beat later; placing is cheap and never doubles up */
      let pending = 0;
      const observer = new MutationObserver((records) => {
        const added = records.some((r) => Array.from(r.addedNodes).some((n) => n.nodeType === 1 && !n.classList.contains('pkh-pumpkin')));
        if (!added || pending) return;
        pending = setTimeout(() => { pending = 0; place(); }, 120);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    /* ---- placing ---- */
    placeAll() {
      const path = window.location.pathname;
      (this.config.pumpkins || []).forEach((p) => {
        if (!p.id) return;
        if (p.page && path.indexOf(p.page) === -1) return;
        if (p.anchor === 'inline') {
          if (!p.marker) return;
          document.querySelectorAll('[data-pumpkin="' + p.marker + '"]').forEach((marker) => {
            if (marker.dataset.pkhDone) return;
            marker.dataset.pkhDone = '1';
            marker.classList.add('pkh-marker');
            marker.appendChild(this.makePumpkin(p, true, marker));
          });
          return;
        }
        const multi = !!MULTI[p.anchor];
        if (!multi && document.querySelector('.pkh-pumpkin[data-pkh-id="' + p.id + '"]')) return;
        if (p.anchor === 'search') { this.placeInSearch(p); return; }
        const sel = p.anchor === 'custom' ? p.selector : ANCHORS[p.anchor];
        if (!sel) return;
        let hosts;
        try { hosts = Array.from(document.querySelectorAll(sel)); } catch (e) { hosts = []; }
        if (p.match) hosts = hosts.filter((h) => saysText(h, p.match));
        if (!multi) hosts = hosts.slice(Math.max(0, (p.nth || 1) - 1), Math.max(0, (p.nth || 1) - 1) + 1);
        hosts.forEach((host) => {
          if (host.querySelector(':scope > .pkh-pumpkin[data-pkh-id="' + p.id + '"]')) return;
          this.mount(p, host);
        });
      });
    }

    /* on the search page, when the query says one of the hunt's search words: a tile at the front
       of the results grid, or inside the "no results" message when nothing else matched */
    placeInSearch(p) {
      if (!/\/search\/?$/.test(window.location.pathname)) return;
      let q = '';
      try { q = new URLSearchParams(window.location.search).get('q') || ''; } catch (e) { q = ''; }
      const query = squash(q);
      if (!query) return;
      const words = String(this.config.searchWords || '').split(',').map(squash).filter(Boolean);
      if (!words.some((w) => query.indexOf(w) > -1)) return;

      const tile = document.createElement('div');
      tile.className = 'pkh-search';
      const spot = document.createElement('div');
      spot.className = 'pkh-search__spot';
      spot.setAttribute('data-pkh-badge', '');
      tile.appendChild(spot);
      if (this.config.searchCaption) {
        const cap = document.createElement('p');
        cap.className = 'pkh-search__caption';
        cap.textContent = this.config.searchCaption;
        tile.appendChild(cap);
      }
      spot.appendChild(this.makePumpkin(p, true, spot));

      const grid = document.querySelector('#product-grid');
      const empty = document.querySelector('.collection-empty');
      if (grid) {
        const li = document.createElement('li');
        li.className = 'column pkh-search__item';
        li.appendChild(tile);
        grid.insertBefore(li, grid.firstElementChild);
      } else if (empty) {
        tile.classList.add('pkh-search--empty');
        empty.insertBefore(tile, empty.firstChild);
      }
    }

    mount(p, host) {
      /* inline modes flow with the host's text: right after it (the stars, a heading) or before it */
      if (p.mode === 'end' || p.mode === 'start') {
        const el = this.makePumpkin(p, true, host);
        if (p.mode === 'start') host.insertBefore(el, host.firstChild);
        else host.appendChild(el);
        return;
      }
      if (getComputedStyle(host).position === 'static') host.classList.add('pkh-anchor');
      const el = this.makePumpkin(p, false, host);
      el.style.left = p.x + '%';
      el.style.top = p.y + '%';
      host.appendChild(el);
    }

    makePumpkin(p, inline, host) {
      /* a button, unless the host already sits inside one (the review cards are buttons): then a span with the role */
      const nested = host && host.closest('button, a');
      const btn = document.createElement(nested ? 'span' : 'button');
      if (nested) {
        btn.setAttribute('role', 'button');
        btn.tabIndex = 0;
        btn.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); btn.click(); } });
      } else {
        btn.type = 'button';
      }
      btn.className = 'pkh-pumpkin' + (inline ? ' pkh-pumpkin--inline' : '') + (this.found.indexOf(p.id) > -1 ? ' is-found' : '') + (this.config.wobble ? ' pkh-wobble' : '');
      btn.dataset.pkhId = p.id;
      btn.setAttribute('aria-label', 'A hidden pumpkin');
      const size = p.size || this.config.size || 44;
      /* inline ones follow the text size unless this pumpkin has its own; a product-card badge marker
         is sized by the hunt's badge settings instead */
      const badge = host && host.hasAttribute('data-pkh-badge');
      if (!badge && (!inline || p.size)) {
        btn.style.width = size + 'px';
        btn.style.height = size + 'px';
      }
      if (p.flip) btn.classList.add('is-flipped');
      if (inline && p.nudge) btn.style.setProperty('--pkh-nudge', p.nudge + 'px');
      if (inline && p.nudgeX) btn.style.setProperty('--pkh-nudge-x', p.nudgeX + 'px');
      if (p.peek && p.peek !== 'none' && p.peekAmount > 0) {
        const a = p.peekAmount + '%';
        const inset = { left: '0 0 0 ' + a, right: '0 ' + a + ' 0 0', top: a + ' 0 0 0', bottom: '0 0 ' + a + ' 0' }[p.peek];
        if (inset) btn.style.clipPath = 'inset(' + inset + ')';
      }
      /* a random start to the wobble, so several on one page do not move in step */
      btn.style.animationDelay = (Math.random() * 5).toFixed(2) + 's';
      btn.appendChild(this.art.content.cloneNode(true));
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.open(btn, p); });
      /* the review marquee and other hosts watch pointer presses; a press on the pumpkin is ours */
      btn.addEventListener('pointerdown', (e) => e.stopPropagation());
      this.placed.push(btn);
      return btn;
    }

    /* ---- the reveal ---- */
    open(btn, p) {
      if (!this.stage.hidden) return;
      const from = btn.getBoundingClientRect();
      const wasFound = this.found.indexOf(p.id) > -1;
      if (!wasFound) {
        this.found.push(p.id);
        if (!this.config.designMode) this.writeFound();
        /* every copy of this pumpkin (a menu card exists twice) shows as found */
        this.placed.forEach((b) => { if (b.dataset.pkhId === p.id) b.classList.add('is-found'); });
      }
      this.fillCard(p);

      /* the page holds still underneath */
      const sbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty('--pkh-sbw', sbw + 'px');
      document.documentElement.classList.add('pkh-open');

      this.stage.hidden = false;
      this.stage.classList.remove('is-shown', 'is-carded', 'is-pinned');
      this.flyer.innerHTML = '';
      this.flyer.appendChild(this.art.content.cloneNode(true));
      this.flyer.classList.toggle('is-flipped', !!p.flip);
      /* the flyer starts exactly over the tapped pumpkin (its clip is not copied: it is whole now) */
      const size = Math.max(from.width, from.height);
      Object.assign(this.flyer.style, { left: from.left + 'px', top: from.top + 'px', width: from.width + 'px', height: from.height + 'px', transform: 'none' });
      this.flyer.hidden = false;

      /* measure where the pin will be once the card has settled: the card waits 48px up and a little
         squashed before its drop, so it is measured with that transform lifted (and no transition, so
         lifting it does not animate) */
      this.card.style.visibility = 'hidden';
      this.card.style.transition = 'none';
      this.card.style.transform = 'none';
      this.card.hidden = false;
      const pinRect = this.pin.getBoundingClientRect();
      this.card.style.transform = '';
      this.card.style.visibility = '';
      void this.card.offsetHeight;
      this.card.style.transition = '';
      const dx = pinRect.left + pinRect.width / 2 - (from.left + from.width / 2);
      const dy = pinRect.top + pinRect.height / 2 - (from.top + from.height / 2);
      const scale = PIN / size;

      requestAnimationFrame(() => {
        this.stage.classList.add('is-shown');
        this.flyer.style.transform = 'translate(' + dx + 'px, ' + dy + 'px) scale(' + scale + ')';
      });
      /* the pin takes over from the flyer the instant the card has finished dropping, so the
         pumpkin never shifts: the flyer sits exactly where the pin lands */
      const onDropped = (e) => {
        if (e && e.target !== this.card) return;
        this.card.removeEventListener('transitionend', onDropped);
        if (!this.stage.classList.contains('is-carded')) return;
        this.stage.classList.add('is-pinned');
        this.flyer.hidden = true;
      };
      const onFlown = () => {
        this.flyer.removeEventListener('transitionend', onFlown);
        if (this.stage.classList.contains('is-carded')) return;
        this.stage.classList.add('is-carded');
        this.card.addEventListener('transitionend', onDropped);
        setTimeout(() => onDropped(), 650);
        const focus = this.card.querySelector('[data-pkh-close]');
        if (focus) focus.focus({ preventScroll: true });
      };
      this.flyer.addEventListener('transitionend', onFlown);
      /* no transition (reduced motion): move on regardless */
      setTimeout(onFlown, 700);
    }

    close() {
      if (this.stage.hidden) return;
      this.stage.classList.remove('is-shown', 'is-carded', 'is-pinned');
      setTimeout(() => {
        this.stage.hidden = true;
        this.card.hidden = true;
        this.flyer.hidden = true;
        document.documentElement.classList.remove('pkh-open');
        document.documentElement.style.removeProperty('--pkh-sbw');
      }, 260);
    }

    /* the card for the pumpkin just tapped: its code, and the bonus once every pumpkin has been found */
    fillCard(current) {
      const pumpkins = (this.config.pumpkins || []).filter((p) => p.id);
      const total = pumpkins.length;
      const n = this.found.length;
      const all = total > 0 && n >= total;

      const box = this.card.querySelector('[data-pkh-coupons]');
      if (box) {
        box.innerHTML = '';
        const row = (code, desc, kind, note) => {
          const el = document.createElement('div');
          el.className = 'pkh-coupon' + (kind ? ' pkh-coupon--' + kind : '');
          const text = document.createElement('span');
          text.className = 'pkh-coupon__text';
          const c = document.createElement('span');
          c.className = 'pkh-coupon__code';
          c.textContent = code;
          text.appendChild(c);
          if (desc) {
            const d = document.createElement('span');
            d.className = 'pkh-coupon__desc';
            d.textContent = desc;
            text.appendChild(d);
          }
          /* a small print line, e.g. "One time use", from the block or the hunt's default */
          if (note) {
            const n = document.createElement('span');
            n.className = 'pkh-coupon__note';
            n.textContent = note;
            text.appendChild(n);
          }
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'pkh-coupon__copy';
          btn.dataset.pkhCopy = code;
          btn.dataset.label = this.config.copyLabel || 'Copy';
          btn.dataset.copied = this.config.copiedLabel || 'Copied';
          btn.textContent = btn.dataset.label;
          el.appendChild(text);
          el.appendChild(btn);
          box.appendChild(el);
        };
        const heading = (label) => {
          if (!label) return;
          const h = document.createElement('div');
          h.className = 'pkh-card__group';
          h.textContent = label;
          box.appendChild(h);
        };
        /* only this pumpkin's code; a shopper gets an earlier one back by tapping that pumpkin again */
        if (current && current.code) row(current.code, current.desc, 'new', current.note || this.config.noteDefault);
        const bonus = this.config.bonus || {};
        if (all && bonus.code) {
          heading(this.config.bonusLabel);
          row(bonus.code, bonus.desc, 'bonus', bonus.note || this.config.noteDefault);
        }
      }
      const title = this.card.querySelector('[data-pkh-title]');
      if (title) title.textContent = (all && title.dataset.titleAll) ? title.dataset.titleAll : title.dataset.title;
      const outro = this.card.querySelector('[data-pkh-outro]');
      if (outro) outro.textContent = (all && outro.dataset.outroAll) ? outro.dataset.outroAll : outro.dataset.outro;
      const tally = this.card.querySelector('[data-pkh-tally]');
      if (tally) {
        tally.innerHTML = '';
        for (let i = 0; i < total; i++) {
          const dot = document.createElement('span');
          dot.className = 'pkh-dot' + (i < n ? ' is-on' : '');
          tally.appendChild(dot);
        }
        const label = document.createElement('span');
        label.className = 'pkh-tally__label';
        label.textContent = n + ' of ' + total + ' found';
        tally.appendChild(label);
      }
      this.pin.innerHTML = '';
      this.pin.appendChild(this.art.content.cloneNode(true));
    }

    copy(btn) {
      const done = () => {
        btn.textContent = btn.dataset.copied;
        btn.classList.add('is-copied');
        clearTimeout(btn._t);
        btn._t = setTimeout(() => { btn.textContent = btn.dataset.label; btn.classList.remove('is-copied'); }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(btn.dataset.pkhCopy).then(done, done);
      else done();
    }

    /* ---- memory ---- */
    readFound() {
      try { const v = JSON.parse(window.localStorage.getItem(STORE) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; }
    }

    writeFound() {
      try { window.localStorage.setItem(STORE, JSON.stringify(this.found)); } catch (e) { /* private mode: the count simply resets */ }
    }
  }

  customElements.define('pumpkin-hunt', PumpkinHunt);
})();
